import * as Api from '@ViewModels';
import { CalendarIcon } from '@WebComponents/svgs/icons/CalendarIcon';
import { css } from 'aphrodite';
import { FC } from 'react';
import { NavLink } from 'react-router-dom';
import { useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../../models/Logging';
import { useMeetingPrepBriefsQuery } from '../../../../../../queries/MeetingPrep/useMeetingPrepBriefsQuery';
import {
	selectBoundMeetingPrepTemplate,
	useMeetingPrepTemplatesQuery,
} from '../../../../../../queries/MeetingPrep/useMeetingPrepTemplates';
import { Button } from '../../../../../components-v2/Button';
import { ROUTES } from '../../../../../constants/routes';
import {
	briefAttendeeSummary,
	briefMeetingTime,
	briefMeetingTitle,
} from '../../../../../pages/MeetingPrepBriefs/helpers';
import { getScheduleTimezone } from '../../../../settings/PersonalSettings/EmailExecAssistantSettings/scheduleUtils';
import { AgentPanelFooter, AgentPanelItem, AgentPanelLink, AgentPanelMessage } from '../../AgentPanel';
import { agentTileState } from '../../AgentTile';
import { styleSheet } from '../../styles';
import { briefExcerpt } from '../EmailExec/helpers';
import { briefingDay, briefsInView, hasEverDelivered, meetingLengthLabel, nextDeliveryLabel } from './helpers';

const TELEMETRY_CATEGORY = 'DashboardMeetingPrepRow';
const PANEL_ITEM_LIMIT = 3;

export const useMeetingPrepAgent = () => {
	const userSession = useUserSession();
	const { logEvent } = useEventLogging(TELEMETRY_CATEGORY);
	const enabled = !!userSession.account?.features?.meetingPrep?.enabled;
	const timeZone = getScheduleTimezone(userSession.user);
	const templatesQuery = useMeetingPrepTemplatesQuery({
		enabled,
		meta: { errorLogging: { category: TELEMETRY_CATEGORY, action: 'DashboardMeetingPrepRowLoadTemplates-Error' } },
	});
	const template = selectBoundMeetingPrepTemplate(templatesQuery.data ?? []);
	const briefsQuery = useMeetingPrepBriefsQuery({
		enabled: !!template,
		meta: { errorLogging: { category: TELEMETRY_CATEGORY, action: 'LoadBriefs-Error' } },
	});

	if (!enabled || templatesQuery.isError || (!templatesQuery.isPending && !template)) {
		return null;
	}

	const agent = {
		key: 'meetingPrep',
		name: 'Meeting Prep',
		icon: <CalendarIcon />,
		iconStyles: styleSheet.meetingPrepIcon,
	};
	if (!template || briefsQuery.isPending) {
		return { ...agent, tile: null, panel: null };
	}
	if (briefsQuery.isError) {
		return {
			...agent,
			tile: agentTileState('Couldn’t load', { isError: true }),
			panel: (
				<AgentPanelMessage
					action={
						<Button size='sm' variant='secondary' onClick={() => briefsQuery.refetch()}>
							Try again
						</Button>
					}
				>
					We couldn’t load your meeting briefs.
				</AgentPanelMessage>
			),
		};
	}

	const briefs = briefsQuery.data.pages.flatMap(page => page.values ?? []);
	const goToBriefs = (from: string) => logEvent('ViewBriefsClicked', { from });
	const goToSettings = (from: string) => logEvent('SettingsClicked', { from });

	const day = briefingDay(briefs, timeZone);
	if (day) {
		const count = day.briefs.length;
		return {
			...agent,
			tile: agentTileState(`${count === 1 ? 'brief' : 'briefs'} for ${day.name}`, { count }),
			panel: <MeetingPrepDayPanel briefs={day.briefs} timeZone={timeZone} />,
		};
	}

	const settingsLink = (from: string) => (
		<AgentPanelFooter>
			<AgentPanelLink to={ROUTES.meetingPrep.settings} onClick={() => goToSettings(from)}>
				Settings
			</AgentPanelLink>
		</AgentPanelFooter>
	);
	const hasDelivered = hasEverDelivered(template);

	if (!template.scheduleEnabled && hasDelivered) {
		return {
			...agent,
			tile: agentTileState('Delivery is paused'),
			panel: (
				<>
					<AgentPanelMessage>You won’t get meeting briefs until it’s back on.</AgentPanelMessage>
					{settingsLink('pausedRow')}
				</>
			),
		};
	}

	if (!template.scheduleEnabled) {
		return {
			...agent,
			tile: agentTileState('Delivery is off'),
			panel: (
				<AgentPanelMessage
					action={
						<Button size='sm' asChild>
							<NavLink to={ROUTES.meetingPrep.settings} onClick={() => goToSettings('firstRun')}>
								Turn on delivery
							</NavLink>
						</Button>
					}
				>
					Turn on delivery to start getting briefs before your meetings.
				</AgentPanelMessage>
			),
		};
	}

	const deliveryLabel = nextDeliveryLabel(template, timeZone);
	if (!hasDelivered) {
		return {
			...agent,
			tile: agentTileState(`First briefs ${deliveryLabel}`),
			panel: (
				<>
					<AgentPanelMessage>{`Your first briefs arrive ${deliveryLabel}.`}</AgentPanelMessage>
					{settingsLink('firstRun')}
				</>
			),
		};
	}

	return {
		...agent,
		tile: agentTileState('No meetings prepped today'),
		panel: (
			<>
				<AgentPanelMessage>{`Next briefs arrive ${deliveryLabel}.`}</AgentPanelMessage>
				<AgentPanelFooter>
					<AgentPanelLink to={ROUTES.meetingPrep.briefs} onClick={() => goToBriefs('quietRow')}>
						View past briefs
					</AgentPanelLink>
				</AgentPanelFooter>
			</>
		),
	};
};

const MeetingPrepDayPanel: FC<{ briefs: Api.IMeetingPrepBrief[]; timeZone: string }> = ({ briefs, timeZone }) => {
	const { logEvent } = useEventLogging(TELEMETRY_CATEGORY);
	const now = Date.now();
	return (
		<>
			{briefsInView(briefs, PANEL_ITEM_LIMIT).map(brief => {
				const attendees = briefAttendeeSummary(brief);
				const hasStarted = new Date(brief.meetingStartTime).getTime() <= now;
				const length = meetingLengthLabel(brief);
				return (
					<AgentPanelItem
						key={brief.id}
						lead={
							<span className={css(styleSheet.timeLead)}>
								<span className={css(styleSheet.timeText, hasStarted && styleSheet.timePast)}>
									{briefMeetingTime(brief, timeZone)}
								</span>
								{length ? <span className={css(styleSheet.timeLength)}>{length}</span> : null}
							</span>
						}
						title={attendees || briefMeetingTitle(brief)}
						meta={attendees ? briefMeetingTitle(brief) : undefined}
						detail={brief.prepSummary ? briefExcerpt(brief.prepSummary) : undefined}
						action={
							<Button variant='secondary' size='sm' asChild>
								<NavLink
									to={
										brief.id
											? `${ROUTES.meetingPrep.briefs}/${encodeURIComponent(brief.id)}`
											: ROUTES.meetingPrep.briefs
									}
									onClick={() => logEvent('ViewBriefClicked', { from: 'dashboardPanel' })}
								>
									Open brief
								</NavLink>
							</Button>
						}
					/>
				);
			})}
			<AgentPanelFooter>
				<AgentPanelLink
					to={ROUTES.meetingPrep.briefs}
					onClick={() => logEvent('ViewBriefsClicked', { from: 'dashboardPanel' })}
				>
					All briefs
				</AgentPanelLink>
				<AgentPanelLink
					to={ROUTES.meetingPrep.settings}
					onClick={() => logEvent('SettingsClicked', { from: 'dashboardPanel' })}
				>
					Settings
				</AgentPanelLink>
			</AgentPanelFooter>
		</>
	);
};
